/**
 * `<policy-editor-rowfilter>` — the `RowFilterPolicy` body (`SC-F512`, `SC-F513`,
 * `SC-S511`).
 *
 * Four things this editor owns beyond the shared matcher:
 *
 *  1. **The filter is exactly one of `predicate` and `expression`.** Populating both,
 *     or neither, is a decode error re-checked at compile. The mode is a radio, so
 *     the both-populated shape is unreachable from here; an imported object carrying
 *     the restricted-CEL form is shown and preserved, because the CEL mode is
 *     `SC-F514` and this surface does not yet author it.
 *  2. **The predicate tree**, which is the most complex widget in the product and
 *     lives in `<policy-predicate>`.
 *  3. **`combine` decides how *this* policy attaches**, not how the earlier ones
 *     combined — so `permissive` ORs and therefore widens what each earlier filter
 *     alone allowed. The fold panel shows where this policy lands in the order.
 *  4. **The emitted-SQL preview**, always visible, labelled advisory. `03` §3.3 calls
 *     it load-bearing: it is the only way an author can check `Like` keeping its
 *     metacharacters, the three literal functions, and `Matches` being partial,
 *     without deploying.
 *
 * Its `exclude` is evaluated **per table**, so excluding one table never lifts
 * protection from the others in a join — with one asymmetry the schema states and
 * this editor repeats: a *subject-only* exclude clause still drops the policy for
 * every table.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "../../components/index.js";
import type { PolicyFinding } from "../../api/policies.js";
import type { BindingSummary } from "./bindings.js";
import { emptyPredicate, type Predicate, type RowFilterSpec, type Selector } from "./model.js";
import { diagnosticSeverity, findingsAt, worst } from "./policy-findings.js";
import "./policy-fold.js";
import "./policy-predicate.js";
import type { PredicateChangeDetail } from "./policy-predicate.js";
import "./policy-selector.js";
import type { SelectorChangeDetail } from "./policy-selector.js";
import type { SpecChangeDetail } from "./editor-sqlaccess.js";
import { describeValues, predicateSql, type FoldMember } from "./sql-preview.js";
import type { PolicySchema } from "./schema.js";
import type { SchemaCache } from "./schema-cache.js";

@customElement("policy-editor-rowfilter")
export class PolicyEditorRowFilter extends LitElement {
  @property({ attribute: false }) schema!: PolicySchema;
  @property({ attribute: false }) spec: RowFilterSpec = { match: {}, filter: {} };
  @property({ attribute: false }) findings: PolicyFinding[] = [];
  @property({ attribute: false }) cache: SchemaCache = {
    tables: [],
    degraded: true,
    warning: "",
    fetchedAt: "",
  };
  @property({ attribute: false }) bindings: BindingSummary[] = [];
  @property({ type: Number }) bindingTotal = 0;
  @property({ attribute: false }) availableTags: string[] = [];
  /** Every row filter in the draft, for the fold panel. */
  @property({ attribute: false }) foldMembers: FoldMember[] = [];
  /** This policy's name and priority, so the fold panel moves as they are edited. */
  @property() selfName = "";
  @property({ type: Number }) selfPriority = 0;
  @property({ type: Boolean }) selfShadow = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    section {
      margin-bottom: var(--bino-space-xl);
    }
    h3 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    fieldset {
      border: none;
      margin: 0 0 var(--bino-space-md);
      padding: 0;
    }
    legend {
      font-size: var(--bino-font-size-base);
      font-weight: 500;
      color: var(--bino-text-muted);
      padding: 0;
      margin-bottom: 0.375rem;
    }
    .radios {
      display: grid;
      gap: var(--bino-space-xs);
    }
    label.radio {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      cursor: pointer;
    }
    label.radio.off {
      opacity: 0.7;
      cursor: not-allowed;
    }
    label.radio input {
      margin-top: 0.25rem;
      accent-color: var(--bino-primary);
    }
    label.radio input:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
      border-radius: var(--bino-radius-sm);
    }
    label.radio .desc {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .tree {
      overflow-x: auto;
    }
    .sql {
      margin-top: var(--bino-space-md);
    }
    .sql pre {
      margin: var(--bino-space-xs) 0 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      background: var(--bino-code-bg);
      color: var(--bino-code-text);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-sm);
      overflow-x: auto;
      white-space: pre;
    }
    .advisory {
      display: block;
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
    }
    .params {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      overflow-wrap: anywhere;
    }
    .alert {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-xs);
      margin: var(--bino-space-xs) 0;
      font-size: var(--bino-font-size-sm);
      max-width: 80ch;
    }
    .alert.error {
      color: var(--bino-error);
    }
    .alert.warn {
      color: var(--bino-warning);
    }
    .rule {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
    }
  `;

  /** A finding about the filter as a whole — which of the two forms is populated is a
   * property of the pair, not of either field — as a live region rather than an empty
   * labelled field. */
  private renderAlert(finding: PolicyFinding) {
    return html`<p class="alert ${diagnosticSeverity(finding.severity) === "error" ? "error" : "warn"}" role="alert">
      <code class="rule">${finding.rule}</code> ${finding.message}
    </p>`;
  }

  private emit(patch: Partial<RowFilterSpec>) {
    this.dispatchEvent(
      new CustomEvent<SpecChangeDetail<RowFilterSpec>>("spec-change", {
        detail: { spec: { ...this.spec, ...patch } },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Which of the two filter forms is populated. An empty expression counts as
   * unset, exactly as the decoder reads it. */
  private get filterMode(): "predicate" | "expression" {
    return (this.spec.filter.expression ?? "").trim() !== "" ? "expression" : "predicate";
  }

  private renderFilterMode() {
    const mode = this.filterMode;
    const predicateHelp = this.schema.help("filterSpec", "predicate");
    const expressionHelp = this.schema.help("filterSpec", "expression");
    return html`<fieldset>
      <legend>Filter form</legend>
      <div class="radios">
        <label class="radio">
          <input
            type="radio"
            name="filter-mode"
            .checked=${mode === "predicate"}
            @change=${() =>
              this.emit({ filter: { predicate: this.spec.filter.predicate ?? emptyPredicate() } })}
          />
          <span>
            <strong>Structured predicate</strong>
            <span class="desc">${predicateHelp}</span>
          </span>
        </label>
        <label class="radio ${mode === "expression" ? "" : "off"}">
          <input
            type="radio"
            name="filter-mode"
            .checked=${mode === "expression"}
            ?disabled=${mode !== "expression"}
          />
          <span>
            <strong>Restricted CEL expression</strong>
            <span class="desc">
              ${expressionHelp} Authoring it is a later stage; an imported expression is shown below
              and published unchanged, and switching to the structured form would discard it.
            </span>
          </span>
        </label>
      </div>
    </fieldset>`;
  }

  private renderFilter() {
    // The exactly-one-of constraint is a property of the PAIR, so the API may anchor
    // it to either half; both are collected and the worst rung shown once.
    const finding = worst([
      ...findingsAt(this.findings, "spec.filter"),
      ...findingsAt(this.findings, "spec.filter.predicate"),
    ]);
    if (this.filterMode === "expression") {
      // `SC-F510`'s linter covers this surface too, and its rules (L36–L40) are
      // the ones that matter most on an IMPORTED expression: the restricted subset
      // rejects arithmetic, every call, every macro, a query.* reference and a
      // column-to-column comparison at COMPILE time, which fails the whole
      // snapshot and leaves the instance serving its previous policy set. Showing
      // the findings inline is what turns that from an invisible publish failure
      // into something an author can read on the expression they cannot yet edit.
      const expression = this.spec.filter.expression ?? "";
      const inline = findingsAt(this.findings, "spec.filter.expression").map((f) => ({
        from: 0,
        to: expression.length,
        severity: f.severity,
        message: f.message,
        ruleId: f.rule,
      }));
      return html`
        ${finding ? this.renderAlert(finding) : nothing}
        <bn-code
          language="cel"
          label="Row filter expression"
          .value=${expression}
          .diagnostics=${inline}
        ></bn-code>
      `;
    }
    const predicate = this.spec.filter.predicate ?? emptyPredicate();
    return html`
      ${finding ? this.renderAlert(finding) : nothing}
      <p class="lede">${this.schema.groupHelp("predicate")}</p>
      <div class="tree">
        <policy-predicate
          root
          .schema=${this.schema}
          .node=${predicate}
          path="spec.filter.predicate"
          .findings=${this.findings}
          .cache=${this.cache}
          .bindings=${this.bindings}
          .bindingTotal=${this.bindingTotal}
          .claimsHelp=${this.schema.groupHelp("claimPaths")}
          @predicate-change=${(e: CustomEvent<PredicateChangeDetail>) => {
            e.stopPropagation();
            this.emit({ filter: { predicate: e.detail.node } });
          }}
        ></policy-predicate>
      </div>
      ${this.renderSql(predicate)}
    `;
  }

  /** M1: the emitted SQL. Parameters are elided as `$N` and the literals are listed
   * beside it, because **every value binds as a parameter and nothing is interpolated
   * into SQL text** — a preview that inlined them would teach the opposite. */
  private renderSql(predicate: Predicate) {
    const sql = predicateSql(predicate);
    const params = describeValues(predicate);
    return html`<div class="sql">
      <bn-panel eyebrow="Emitted SQL">
        <span class="advisory">Advisory preview — the instance's engine decides.</span>
        <pre>${sql || "— no complete condition yet —"}</pre>
        ${params.length > 0
          ? html`<p class="params">
              Parameters, in order: ${params.map((p, i) => `$${i + 1} = ${p}`).join(", ")}. Every value
              binds as a parameter; nothing is interpolated into SQL text.
            </p>`
          : nothing}
        <p class="params">
          The filter is applied by wrapping each matched table in a subquery —
          <code>FROM (SELECT * FROM &lt;table&gt; WHERE &lt;predicate&gt;) &lt;alias&gt;</code> —
          preserving the original alias across joins, subqueries and CTE bodies.
        </p>
      </bn-panel>
    </div>`;
  }

  private renderCombine() {
    const field = this.schema.field("rowFilterSpec", "combine");
    const options = this.schema.options(field?.enum ?? "Combine");
    const current = this.spec.combine || this.schema.enumDefault(field?.enum ?? "Combine");
    return html`<fieldset>
      <legend>How this filter attaches</legend>
      <div class="radios">
        ${options.map(
          (o) => html`<label class="radio">
            <input
              type="radio"
              name="combine"
              .checked=${current === o.value}
              @change=${() => this.emit({ combine: o.value })}
            />
            <span>
              <strong>${o.label}</strong>
              ${o.description ? html`<span class="desc">${o.description}</span>` : nothing}
            </span>
          </label>`,
        )}
      </div>
      ${field?.help ? html`<p class="lede">${field.help}</p>` : nothing}
    </fieldset>`;
  }

  private get permissiveHelp(): string {
    const values = this.schema.enumOf("Combine")?.values ?? [];
    return values.find((v) => v.value === "permissive")?.help ?? "";
  }

  override render() {
    if (!this.schema) return nothing;
    const kind = this.schema.kind("RowFilterPolicy");
    const excludeHelp = this.schema.help("policyCommon", "exclude");
    const combine = this.spec.combine || this.schema.enumDefault("Combine");
    return html`
      <section>
        <bn-panel eyebrow="How this kind decides">
          <p class="lede">${kind?.help ?? ""}</p>
        </bn-panel>
      </section>

      <section>
        <h3>Who and what it applies to</h3>
        <policy-selector
          .schema=${this.schema}
          .selector=${this.spec.match}
          path="spec.match"
          .cache=${this.cache}
          .findings=${this.findings}
          .bindings=${this.bindings}
          .availableTags=${this.availableTags}
          @selector-change=${(e: CustomEvent<SelectorChangeDetail>) => {
            e.stopPropagation();
            this.emit({ match: e.detail.selector });
          }}
        ></policy-selector>
      </section>

      <section>
        <h3>The filter</h3>
        ${this.renderFilterMode()} ${this.renderFilter()}
      </section>

      <section>
        <h3>Composition with other row filters</h3>
        ${this.renderCombine()}
        <bn-panel eyebrow="Fold order">
          <policy-fold-order
            .members=${this.foldMembers}
            .selfName=${this.selfName}
            .selfPriority=${this.selfPriority}
            .selfCombine=${combine}
            .selfShadow=${this.selfShadow}
            .permissiveHelp=${this.permissiveHelp}
          ></policy-fold-order>
        </bn-panel>
      </section>

      <section>
        <h3>Exceptions</h3>
        <policy-selector
          is-exclude
          .schema=${this.schema}
          .selector=${this.spec.exclude ?? {}}
          path="spec.exclude"
          .scopeExplainer=${`${excludeHelp} ${
            kind?.excludeScope === "per-table"
              ? "For this kind, exclude is evaluated per table, so excluding one table never lifts protection from the others in a join — with one asymmetry: a subject-only exclude clause still drops the policy for every table."
              : ""
          }`.trim()}
          .cache=${this.cache}
          .findings=${this.findings}
          .bindings=${this.bindings}
          .availableTags=${this.availableTags}
          @selector-change=${(e: CustomEvent<SelectorChangeDetail>) => {
            e.stopPropagation();
            const selector: Selector = e.detail.selector;
            this.emit({
              exclude:
                (selector.any?.length ?? 0) === 0 && (selector.all?.length ?? 0) === 0
                  ? undefined
                  : selector,
            });
          }}
        ></policy-selector>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-editor-rowfilter": PolicyEditorRowFilter;
  }
}
